import React, { useEffect, useState } from "react";
import ImageUploaderLocal from "./ImageUploaderLocal";
import ImageThumbnail from "./ImageThumbnail";

interface Image {
  id: string;
  image: string;
}
interface IProps {
  name?: string;
  containerClassName?: string;
  images?: { id: string; image: string }[];
  onChange?: (value: File[], name: string) => void;
  onBlur?: (name: string) => void;
  multiple?: boolean;
  labelName?: string;
  onRemove?: (id: string) => void;
  setInProgress?: (value: boolean) => void;
  maxFileSize?: number; // in bytes
}

const SimpleImageUploader = ({
  name = "",
  containerClassName,
  images = [],
  onChange,
  onBlur,
  multiple,
  labelName,
  onRemove,
  ...props
}: IProps) => {
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [uploadedImages, setUploadedImages] = useState<Image[]>([]);

  useEffect(() => {
    return () => setUploadedImages([]);
  }, []);

  useEffect(() => {
    if (!multiple && selectedImages.length > 0) {
      setSelectedImages([]);
    }

    setUploadedImages((prev) => {
      const hasChanged =
        prev.length !== images.length ||
        prev.some(
          (img, i) => img.id !== images[i]?.id || img.image !== images[i]?.image
        );

      return hasChanged ? images : prev;
    });
  }, [images, multiple]);

  const handleFileChanged = async (filesList: File[] | FileList) => {
    !multiple && setSelectedImages([]);
    const files = Array.from(filesList);
    const newState = [...selectedImages, ...files];
    setSelectedImages(newState);
    onChange && onChange(newState, name);
  };

  useEffect(() => {
    onBlur && onBlur(name);
  }, [selectedImages]);

  const removeUploadedImages = (id: string) => {
    const images = uploadedImages.filter((item) => item.id !== id);
    setUploadedImages(images);
    onRemove && onRemove(id);
  };

  function showUploadedImage(index: number, item: Image) {
    if (!item.image) return;
    return (
      <ImageThumbnail
        key={index}
        imageUrl={item.image}
        onRemove={() => removeUploadedImages(String(item.id))}
      />
    );
  }

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    handleFileChanged(e.target.files);
  };

  const onRemoveSelected = (file: File) => {
    const filteredItems = selectedImages.filter((item) => item != file);
    setSelectedImages([...filteredItems]);
    onChange && onChange(filteredItems, name);
  };

  return (
    <ImageUploaderLocal
      name={name}
      labelName={labelName}
      multiple={multiple}
      containerClassName={containerClassName ?? ""}
      onChange={handleOnChange}
      onDraged={handleFileChanged}
      onRemoveSelected={onRemoveSelected}
      uploadedImagesLength={uploadedImages.length}
      setInProgress={props.setInProgress}
      {...props}
    >
      {uploadedImages &&
        uploadedImages.length > 0 &&
        uploadedImages?.map((item: Image, index: number) =>
          showUploadedImage(index, item)
        )}
    </ImageUploaderLocal>
  );
};

export default SimpleImageUploader;
